import { useLocalStorage } from '@vueuse/core';
import type { Location } from '~/composables/useCmsMedia';
import {
  type UserRole,
  type SubscriptionType,
  AuthVia,
  AuthViaStorageKey,
  type SubscriptionPurchasePlatform,
} from '~/enums/auth.enum';
import sessionHelper from '~/utils/sessionHelper';

export interface User {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  country?: string;
  profession?: string;
  specialty?: string;
  specialtyOther?: string;
  professionalTrack?: string;
  yearsOfExperience?: string;
  profileImage: string;
  email: string;
  roles: UserRole[];
  group?: undefined | {
    groupUuid: string;
    groupName: string;
    memberRole: string;
    memberStatus: string;
  };
  subscription: {
    type: SubscriptionType;
    purchasePlatform?: SubscriptionPurchasePlatform;
  };
  isVerified: boolean;
  blockedFromMailing: boolean;
  registrationCompleted: boolean;
  social?: {
    provider?: string;
    providerId?: string;
  };
  planType: string;
  traceId?: string;
  profilePicture?: Location | null;
  numberOfUnreadNotifications: number;
  createdAt: string;
  accountDeletionStatus?: string | null;
  accountDeletionScheduledAt?: string | null;
}

export default function useAuthentication () {

  const checkAuthenticationStatus = async () => {
    const { user } = useUserStore();
    return !!user.value;
  };

  const getUser = async (updateStore: boolean = true) => {
    try {
      const response = await $fetch('/api/auth/user', { credentials: 'include' });

      if (updateStore) {
        useUserStore().setUser(response as User);
        await useFeatures().refreshFeatures();
      }

      return response as User;
    } catch (error) {
      console.error('[authentication failed]', error);
      await _checkGetUserError(error);
      return null;
    }
  };

  const _checkGetUserError = async (error: unknown) => {
    if (!error || typeof error !== 'object' || !('status' in error)) {
      return;
    }

    if ([403, 500].includes(Number(error.status))) {
      await logout();
      return;
    }

    return;
  };

  const login = async (email: string, password: string) => {
    await useAuthenticatedFetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
      body: {
        email,
        password,
      },
    });
    await getUser();
  };

  const logout = async (location: string | null = '/auth/login', query: Record<string, string> = {}) => {
    try {
      useNuxtApp().$dataLayerReset();
      useNuxtApp().$echoDisconnect();

      await $fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
        onRequest: context => sessionHelper.fetchOnRequestInterceptor(context, true),
      });

      await sessionHelper.resetCsrf();
    } catch (error) {
      console.error(error);
    }

    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('b2bPopupShownThisSession');
    }

    useUserStore().setUser(null);

    if (location) {
      navigateTo({ path: location, query , replace: true });
    }
  };

  const authenticatedVia = async (via: string) => {
    if (via !== AuthVia.MedmasteryApp) {
      throw new Error('Invalid authentication method');
    }

    const { user } = useUserStore();

    if (!user?.value) {
      throw new Error('There is no user logged in');
    }

    if (!user.value.isVerified) {
      navigateTo('/auth/verify-email');
      return;
    }

    if (!user.value.registrationCompleted) {
      navigateTo('/auth/about-you');
      return;
    }

    if (!user?.value?.isVerified || !user?.value?.registrationCompleted) {
      console.warn(`User has not completed ${!user?.value?.isVerified ? 'verification' : 'onboarding'}`);
      return;
    }

    const isNewSignup = useLocalStorage(AuthViaStorageKey.IsNewSignup, null, { listenToStorageChanges: false }).value === 'true';

    useLocalStorage(AuthViaStorageKey.AuthVia, null, { listenToStorageChanges: false }).value = null;
    useLocalStorage(AuthViaStorageKey.IsNewSignup, null, { listenToStorageChanges: false }).value = null;
    useLocalStorage(AuthViaStorageKey.Platform, null, { listenToStorageChanges: false }).value = null;

    const response = await $fetch(`/api/auth/user/token?for=${via}`, {
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
      onRequest: sessionHelper.fetchOnRequestInterceptor,
    });

    await navigateTo('/auth/app-authenticated', { replace: true });

    const authMethod = user.value?.social?.providerId || 'email';

    // Include new_signup flag and auth_method in redirect URL for AppsFlyer tracking
    const redirectUrl = isNewSignup
      ? `medmastery://auth/${encodeURIComponent(response.token)}?new_signup=true&auth_method=${authMethod}`
      : `medmastery://auth/${encodeURIComponent(response.token)}`;

    window.location.href = redirectUrl;
  };

  return {
    checkAuthenticationStatus,
    getUser,
    login,
    logout,
    authenticatedVia,
  };
}
